import { HookOutOfContextError } from "@jugl-web/utils/errors";
import {
  createContext,
  FC,
  PropsWithChildren,
  useContext,
  useMemo,
} from "react";
import { EntityPropsApi } from "./features/entity-props/entityPropsApi";
import { EntitySpacesApi } from "./features/entity-spaces/entitySpacesApi";
import { TasksApi } from "./features/tasks";
import { UsersApi } from "./features/users";
import { WorkspacesApi } from "./features/workspaces";
import { CustomerApi } from "./features/customer";
import { ContactsApi } from "./features/contacts";
import { TicketsApi } from "./features/tickets/ticketsApi";
import { CustomerFormFieldApi } from "./features/customer-form-field";
import { TasksCommentsApi } from "./features/tasks-comments/tasksCommentsApi";
import { DriveApi } from "./features/drive";
import { TasksTemplatesApi } from "./features/tasks-templates";
import { ModuleNotificationsApi } from "./features/module-notifications";
import { AuthApi } from "./features/auth/authApi";
import { EntitiesApi } from "./features/entities/entitiesApi";
import { LinksApi } from "./features/links";
import { OrdersApi } from "./features/orders";
import { InventoryApi } from "./features/inventory/inventoryApi";
import { IntegrationsApi } from "./features/integrations/integrationsApi";
import { DashboardApi } from "./features/dashboard";
import { ChatsApi } from "./features/chats/chatsApi";
import { InvoicesApi } from "./features/invoices/invoicesApi";
import { AIAssistantApi } from "./features/ai-assistant/aiAssistantApi";
import { SubscriptionV2Api } from "./features/subscription";
import { AIWorkflowsApi } from "./features/ai-workflows";
import { ShopifyApi } from "./features/shopify";

interface RestApiContextValue {
  apiBaseUrl: string;
  tasksApi: TasksApi;
  tasksCommentsApi: TasksCommentsApi;
  tasksTemplatesApi: TasksTemplatesApi;
  usersApi: UsersApi;
  workspacesApi: WorkspacesApi;
  entityPropsApi: EntityPropsApi;
  entitySpacesApi: EntitySpacesApi;
  customersApi: CustomerApi;
  contactsApi: ContactsApi;
  ticketsApi: TicketsApi;
  customersFormFieldApi: CustomerFormFieldApi;
  dashboardApi: DashboardApi;
  driveApi: DriveApi;
  moduleNotificationsApi: ModuleNotificationsApi;
  authApi: AuthApi;
  entitiesApi: EntitiesApi;
  linksApi: LinksApi;
  ordersApi: OrdersApi;
  inventoryApi: InventoryApi;
  invoicesApi: InvoicesApi;
  integrationsApi: IntegrationsApi;
  chatsApi: ChatsApi;
  aiAssistantApi: AIAssistantApi;
  subscriptionV2Api: SubscriptionV2Api;
  aiWorkflowsApi: AIWorkflowsApi;
  shopifyApi: ShopifyApi;
}

const RestApiContext = createContext<RestApiContextValue | null>(null);

export const RestApiProvider: FC<
  PropsWithChildren<{ apis: Partial<RestApiContextValue>; apiBaseUrl: string }>
> = ({ children, apis, apiBaseUrl }) => {
  const value = useMemo(
    () => ({ ...apis, apiBaseUrl }) as RestApiContextValue,
    [apis, apiBaseUrl]
  );
  return (
    <RestApiContext.Provider value={value}>{children}</RestApiContext.Provider>
  );
};

export const useRestApiProvider = () => {
  const context = useContext(RestApiContext);

  if (!context) {
    throw new HookOutOfContextError("useRestApiProvider", "RestApiContext");
  }

  return context;
};
